import React, { useState } from 'react';
import { Mail, X, RefreshCw } from 'lucide-react';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const EmailVerifyBanner = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  if (!user) return null;
  if (user.email_verified) return null;
  if (user.role === 'admin') return null;
  if (hidden) return null;

  const resend = async () => {
    setBusy(true);
    try {
      await API.post('/auth/resend-verification');
      toast({
        title: 'Verification email sent',
        description: `Check your inbox at ${user.email}. Link expires in 48 hours.`,
      });
    } catch (err) {
      toast({ title: 'Failed to resend', description: err.response?.data?.detail || 'Please try again in a minute.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="w-full bg-gradient-to-r from-amber-500/95 via-amber-400/95 to-yellow-400/95 text-amber-950 border-b border-amber-700/40"
      data-testid="email-verify-banner"
    >
      <div className="mx-auto max-w-[1240px] px-4 md:px-8 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="h-8 w-8 rounded-full bg-amber-950/10 border border-amber-950/25 flex items-center justify-center shrink-0">
            <Mail size={15} />
          </span>
          <div className="text-[13px] leading-snug">
            <b>Verify your email address</b>
            <span className="hidden sm:inline"> — we sent a verification link to <b>{user.email}</b>. Click the link to unlock full portal features.</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={resend}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-950 hover:bg-amber-900 disabled:opacity-60 text-amber-50 text-[12px] font-semibold px-3.5 py-1.5 transition"
            data-testid="resend-verification-btn"
          >
            <RefreshCw size={12} className={busy ? 'animate-spin' : ''} /> {busy ? 'Sending…' : 'Resend Email'}
          </button>
          <button
            onClick={() => setHidden(true)}
            className="h-8 w-8 rounded-full hover:bg-amber-950/10 flex items-center justify-center text-amber-950/70 hover:text-amber-950"
            aria-label="Dismiss"
            data-testid="dismiss-verify-banner"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerifyBanner;
